// src/pages/dashboard/agent/Dashboard.jsx
import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { formatDateOnly } from '../../../lib/helpers';
import { 
  Users, 
  UserCheck, 
  Clock, 
  Shield, 
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Badge
} from 'lucide-react';
import Card from '../../../components/layout/ui/Card';
import Button from '../../../components/layout/ui/Button';
import { Link } from 'react-router-dom';

export default function AgentDashboard() {
  const { user, api } = useAuth();
  const [stats, setStats] = useState({
    totalNannies: 0,
    pendingVetting: 0,
    vettedToday: 0,
    completedThisMonth: 0
  });
  const [recentNannies, setRecentNannies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAgentData();
  }, []);

  const fetchAgentData = async () => {
    setLoading(true);
    try {
      const [nanniesRes, statsRes] = await Promise.all([
        api.get('/agent/my-nannies'),
        api.get('/agent/stats') // Optional: create this endpoint
      ]);

      const nannies = nanniesRes.data.data || nanniesRes.data || [];
      const statsData = statsRes.data || {};

      setRecentNannies(nannies.slice(0, 6));

      setStats({
        totalNannies: nannies.length,
        pendingVetting: nannies.filter(n => !n.is_vetted).length,
        vettedToday: statsData.vetted_today || 0,
        completedThisMonth: statsData.vetted_this_month || nannies.filter(n => n.is_vetted).length
      });
    } catch (err) {
      console.error('Agent data fetch failed:', err);
      // Fallback mock data for dev
      setRecentNannies([]);
      setStats({ totalNannies: 12, pendingVetting: 5, vettedToday: 3, completedThisMonth: 28 });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-20">Loading agent dashboard...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-gray-900">
          Welcome back, Agent {user?.first_name || 'John'}
        </h1>
        <p className="text-muted mt-2 text-lg">
          Manage and vet nannies assigned to you
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-6 text-center">
          <Users size={48} className="mx-auto text-primary mb-4" />
          <p className="text-4xl font-bold text-primary">{stats.totalNannies}</p>
          <p className="text-muted">Total Assigned</p>
        </Card>

        <Card className="p-6 text-center">
          <Clock size={48} className="mx-auto text-orange-500 mb-4" />
          <p className="text-4xl font-bold text-orange-500">{stats.pendingVetting}</p>
          <p className="text-muted">Pending Vetting</p>
        </Card>

        <Card className="p-6 text-center">
          <CheckCircle size={48} className="mx-auto text-green-600 mb-4" />
          <p className="text-4xl font-bold text-green-600">{stats.vettedToday}</p>
          <p className="text-muted">Vetted Today</p>
        </Card>

        <Card className="p-6 text-center">
          <TrendingUp size={48} className="mx-auto text-secondary mb-4" />
          <p className="text-4xl font-bold text-secondary">{stats.completedThisMonth}</p>
          <p className="text-muted">This Month</p>
        </Card>
      </div>

      {/* Recent Nannies */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Recently Assigned Nannies</h2>
          <Button lin="a" href="/dashboard/agent/nannies">
            View All Nannies
          </Button>
        </div>

        {recentNannies.length === 0 ? (
          <Card className="text-center py-20">
            <AlertCircle size={80} className="mx-auto text-gray-300 mb-6" />
            <h3 className="text-2xl font-semibold mb-3">No nannies assigned yet</h3>
            <p className="text-muted text-lg">New nannies will appear here when assigned to you</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recentNannies.map(nanny => (
              <Card key={nanny.id} className="p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <img
                      src={nanny.profile_picture || '/default-avatar.jpg'}
                      alt={nanny.first_name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div>
                      <h3 className="text-xl font-bold">
                        {nanny.first_name} {nanny.last_name}
                      </h3>
                      <p className="text-muted text-sm">
                        {nanny.phone_number}
                      </p>
                    </div>
                  </div>
                  {nanny.is_vetted ? (
                    <Badge variant="success" className="flex items-center gap-2">
                      <Shield size={16} />
                      Vetted
                    </Badge>
                  ) : (
                    <Badge variant="warning">
                      <Clock size={16} />
                      Pending
                    </Badge>
                  )}
                </div>

                <div className="mt-5 pt-5 border-t border-gray-200 text-sm">
                  <p className="text-gray-600">
                    Assigned: {formatDateOnly(nanny.assigned_at || nanny.created_at)}
                  </p>
                  {nanny.guarantor && (
                    <p className="mt-2">
                      <strong>Guarantor:</strong> {nanny.guarantor.full_name}
                    </p>
                  )}
                </div>

                <div className="mt-5 flex gap-3">
                  {/* 1. Agent Nanny List */}
<Link to={`/dashboard/agent/nanny/${nanny.id}`}>
  <Button className="w-full" variant={nanny.is_vetted ? "outline" : "primary"}>
    {nanny.is_vetted ? 'View Details' : 'Start Vetting Now'}
  </Button>
</Link>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-8 text-center hover:shadow-lg transition-shadow cursor-pointer bg-gradient-to-br from-primary to-secondary text-white">
          <UserCheck size={48} className="mx-auto mb-4" />
          <h3 className="text-xl font-bold mb-2">Vetting Guidelines</h3>
          <p className="text-white/90">Review ID, guarantor & qualifications</p>
        </Card>

        <Card className="p-8 text-center hover:shadow-lg transition-shadow cursor-pointer">
          <Shield size={48} className="mx-auto text-green-600 mb-4" />
          <h3 className="text-xl font-bold mb-2">Mark as Vetted</h3>
          <p className="text-muted">Complete vetting & approve nanny</p>
        </Card>

        <Card className="p-8 text-center hover:shadow-lg transition-shadow cursor-pointer">
          <AlertCircle size={48} className="mx-auto text-orange-500 mb-4" />
          <h3 className="text-xl font-bold mb-2">Flag Issues</h3>
          <p className="text-muted">Report fake documents or concerns</p>
        </Card>
      </div>
    </div>
  );
}